import React, { useState, useMemo, useRef, useCallback } from 'react';
import { Title, Text, Box, CopyButton, Button, Group } from "@mantine/core";

/**
 * Normalizes a number string (handles European formats)
 */
const normalizeNumber = (numStr: string): string => {
  let cleaned = numStr.trim();
  if (!cleaned) return '';

  const hasDot = cleaned.includes('.');
  const hasComma = cleaned.includes(',');

  if (hasDot && hasComma) {
    const lastDot = cleaned.lastIndexOf('.');
    const lastComma = cleaned.lastIndexOf(',');
    if (lastComma > lastDot) {
      // European: 1.200,99 → 1200.99
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } else {
      // US: 1,200.99 → 1200.99
      cleaned = cleaned.replace(/,/g, '');
    }
  } else if (hasComma) {
    const parts = cleaned.split(',');
    if (parts.length === 2 && parts[1].length <= 2) {
      // Decimal comma: 10,99 → 10.99
      cleaned = cleaned.replace(',', '.');
    } else {
      // Thousands: 1,000 → 1000
      cleaned = cleaned.replace(/,/g, '');
    }
  } else if (hasDot) {
    const parts = cleaned.split('.');
    if (parts.length > 2 || (parts.length === 2 && parts[1].length === 3)) {
      // Thousands: 1.000 → 1000
      cleaned = cleaned.replace(/\./g, '');
    }
  }

  return cleaned;
};

/**
 * Cleans input and evaluates as math expression
 */
/**
 * Formats expression for display (adds thousand separators to numbers)
 */
const formatExpression = (expr: string): string => {
  return expr.replace(/\d+\.?\d*/g, (match) => {
    const num = parseFloat(match);
    const [intPart, decPart] = num.toString().split('.');
    const formatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return decPart ? `${formatted},${decPart}` : formatted;
  });
};

const evaluate = (input: string): { cleaned: string; display: string; result: number } | null => {
  try {
    // Strip comments: /* ... */ and // to end of line
    let expr = input
      .replace(/\/\*[\s\S]*?\*\//g, '')
      .replace(/\/\/.*$/gm, '')
      .replace(/,-/g, '')  // Czech price notation: 100,- → 100
      .replace(/[x×]/gi, '*')
      .replace(/÷/g, '/')
      // Handle K/M/B suffixes: 2.5K → 2500, 3M → 3000000, 1B → 1000000000
      .replace(/([\d.,]+)\s*[Kk]/g, (_, num) => {
        const n = parseFloat(normalizeNumber(num));
        return (n * 1000).toString();
      })
      .replace(/([\d.,]+)\s*[Mm]/g, (_, num) => {
        const n = parseFloat(normalizeNumber(num));
        return (n * 1000000).toString();
      })
      .replace(/([\d.,]+)\s*[Bb]/g, (_, num) => {
        const n = parseFloat(normalizeNumber(num));
        return (n * 1000000000).toString();
      })
      // Handle percentages: 1000 + 21% → 1000 * 1.21, 300 - 10% → 300 * 0.9
      .replace(/\+\s*([\d.,]+)\s*%/g, (_, pct) => {
        const p = parseFloat(normalizeNumber(pct));
        return `*${1 + p / 100}`;
      })
      .replace(/-\s*([\d.,]+)\s*%/g, (_, pct) => {
        const p = parseFloat(normalizeNumber(pct));
        return `*${1 - p / 100}`;
      });

    // Keep only valid math chars: digits, operators, parentheses, decimal separators, spaces
    expr = expr.replace(/[^\d+\-*/().,\s]/g, '');

    // Normalize numbers (handle European format)
    // Split by operators, normalize each number, rejoin
    const parts = expr.split(/([+\-*/()])/);
    const normalized = parts.map(part => {
      const trimmed = part.trim();
      // If it looks like a number, normalize it
      if (/^[\d.,\s]+$/.test(trimmed)) {
        return normalizeNumber(trimmed.replace(/\s/g, ''));
      }
      return trimmed;
    }).join('');

    // Remove any remaining spaces
    const final = normalized.replace(/\s/g, '');

    if (!final || !/\d/.test(final)) return null;

    // Validate - only allow numbers and basic operators
    if (!/^[\d.+\-*/()]+$/.test(final)) return null;

    // Evaluate
    const result = new Function(`return (${final})`)();

    if (typeof result !== 'number' || !isFinite(result)) return null;

    return { cleaned: final, display: formatExpression(final), result };
  } catch {
    return null;
  }
};

const highlightSyntax = (text: string): React.ReactNode[] => {
  if (!text) return [];

  const tokens: { type: string; value: string }[] = [];
  let i = 0;

  while (i < text.length) {
    // Block comment /* ... */
    if (text[i] === '/' && text[i + 1] === '*') {
      const end = text.indexOf('*/', i + 2);
      const closeIdx = end === -1 ? text.length : end + 2;
      tokens.push({ type: 'comment', value: text.slice(i, closeIdx) });
      i = closeIdx;
    }
    // Line comment //
    else if (text[i] === '/' && text[i + 1] === '/') {
      const end = text.indexOf('\n', i);
      const closeIdx = end === -1 ? text.length : end;
      tokens.push({ type: 'comment', value: text.slice(i, closeIdx) });
      i = closeIdx;
    }
    // Numbers (with optional decimal separators, suffixes, and %)
    else if (/[\d]/.test(text[i])) {
      let num = '';
      while (i < text.length && /[\d.,\s]/.test(text[i]) && !(text[i] === ' ' && !/\d/.test(text[i + 1] || ''))) {
        num += text[i];
        i++;
      }
      // Absorb suffix (K, M, B) or ,-
      if (i < text.length && /[KkMmBb]/.test(text[i])) {
        num += text[i];
        i++;
      } else if (text[i] === ',' && text[i + 1] === '-') {
        num += ',-';
        i += 2;
      }
      // Absorb %
      if (i < text.length && text[i] === '%') {
        num += '%';
        i++;
      }
      tokens.push({ type: 'number', value: num });
    }
    // Operators
    else if (/[+\-*\/x×÷]/.test(text[i])) {
      tokens.push({ type: 'operator', value: text[i] });
      i++;
    }
    // Parentheses
    else if (/[()]/.test(text[i])) {
      tokens.push({ type: 'paren', value: text[i] });
      i++;
    }
    // Newlines (preserve)
    else if (text[i] === '\n') {
      tokens.push({ type: 'newline', value: '\n' });
      i++;
    }
    // Other text (will be stripped by evaluator)
    else {
      let other = '';
      while (i < text.length && !/[\d+\-*\/x×÷()(\n]/.test(text[i]) && !(text[i] === '/' && (text[i + 1] === '/' || text[i + 1] === '*'))) {
        other += text[i];
        i++;
      }
      tokens.push({ type: 'other', value: other });
    }
  }

  const colorMap: Record<string, string> = {
    number: 'var(--mantine-color-blue-4)',
    operator: 'var(--mantine-color-orange-4)',
    paren: 'var(--mantine-color-yellow-4)',
    comment: 'var(--mantine-color-dimmed)',
    other: 'var(--mantine-color-dimmed)',
  };

  return tokens.map((token, idx) => {
    if (token.type === 'newline') return '\n';
    return (
      <span
        key={idx}
        style={{
          color: colorMap[token.type],
          fontStyle: token.type === 'comment' ? 'italic' : undefined,
        }}
      >
        {token.value}
      </span>
    );
  });
};

const PLACEHOLDER = `Paste anything here - non-math characters will be removed...

Examples:
- 2.5K + 500 → 3000
- 3M / 1.5K → 2000
- Price: 1 000 Kč + tax 200 Kč
- Total: 1.200,99 * 2
- 100+200+300`;

const Calculator = () => {
  const [input, setInput] = useState(() => localStorage.getItem('calculator-input') ?? '');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);

  const handleInputChange = (value: string) => {
    setInput(value);
    localStorage.setItem('calculator-input', value);
  };

  const syncScroll = useCallback(() => {
    if (textareaRef.current && highlightRef.current) {
      highlightRef.current.scrollTop = textareaRef.current.scrollTop;
      highlightRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Tab') return;
    e.preventDefault();

    const ta = e.currentTarget;
    const { selectionStart, selectionEnd, value } = ta;
    const TAB = '  ';

    if (selectionStart === selectionEnd && !e.shiftKey) {
      // No selection - insert 2 spaces at cursor
      const newValue = value.slice(0, selectionStart) + TAB + value.slice(selectionEnd);
      handleInputChange(newValue);
      requestAnimationFrame(() => {
        ta.selectionStart = ta.selectionEnd = selectionStart + TAB.length;
      });
      return;
    }

    // Selection exists or Shift+Tab - indent/dedent affected lines
    const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
    const lineEnd = value.indexOf('\n', selectionEnd);
    const blockEnd = lineEnd === -1 ? value.length : lineEnd;
    const block = value.slice(lineStart, blockEnd);

    let newBlock: string;
    let deltaFirst: number;
    let deltaTotal: number;

    if (e.shiftKey) {
      // Dedent: remove up to 2 leading spaces per line
      deltaTotal = 0;
      deltaFirst = 0;
      let firstLine = true;
      newBlock = block.replace(/^( {1,2})/gm, (match) => {
        deltaTotal += match.length;
        if (firstLine) { deltaFirst = match.length; firstLine = false; }
        return '';
      });
    } else {
      // Indent: add 2 spaces to each line
      const lines = block.split('\n');
      deltaFirst = TAB.length;
      deltaTotal = lines.length * TAB.length;
      newBlock = lines.map(l => TAB + l).join('\n');
    }

    const newValue = value.slice(0, lineStart) + newBlock + value.slice(blockEnd);
    handleInputChange(newValue);

    requestAnimationFrame(() => {
      if (e.shiftKey) {
        ta.selectionStart = Math.max(lineStart, selectionStart - deltaFirst);
        ta.selectionEnd = Math.max(ta.selectionStart, selectionEnd - deltaTotal);
      } else {
        ta.selectionStart = selectionStart + deltaFirst;
        ta.selectionEnd = selectionEnd + deltaTotal;
      }
    });
  }, []);

  const evaluation = useMemo(() => evaluate(input), [input]);
  const highlighted = useMemo(() => highlightSyntax(input), [input]);

  const formatNumber = (num: number) => {
    return num.toLocaleString('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 4 });
  };

  const editorStyles: React.CSSProperties = {
    fontFamily: 'monospace',
    fontSize: '1.2rem',
    lineHeight: '1.55',
    padding: '10px 12px',
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
  };

  return (
    <>
      <Title order={3} mb="md">Calculator</Title>

      <Box
        style={{
          position: 'relative',
          minHeight: '200px',
          borderRadius: 'var(--mantine-radius-sm)',
          border: '1px solid var(--mantine-color-default-border)',
          backgroundColor: 'var(--mantine-color-body)',
        }}
      >
        {/* Highlight layer */}
        <div
          ref={highlightRef}
          style={{
            ...editorStyles,
            position: 'absolute',
            inset: 0,
            overflow: 'hidden',
            pointerEvents: 'none',
            color: 'transparent',
          }}
          aria-hidden
        >
          {input ? highlighted : <span style={{ color: 'var(--mantine-color-placeholder)' }}>{PLACEHOLDER}</span>}
          {/* Trailing newline so height matches textarea */}
          {'\n'}
        </div>

        {/* Transparent textarea */}
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => handleInputChange(e.target.value)}
          onScroll={syncScroll}
          onKeyDown={handleKeyDown}
          placeholder=""
          spellCheck={false}
          style={{
            ...editorStyles,
            position: 'relative',
            width: '100%',
            minHeight: '200px',
            resize: 'vertical',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: 'transparent',
            caretColor: 'var(--mantine-color-text)',
            WebkitTextFillColor: 'transparent',
          }}
        />
      </Box>

      {evaluation && (
        <Box mt="xl">
          <Title order={4} mb="sm">Cleaned Expression</Title>
          <Text c="dimmed" size="lg" mb="md" style={{ fontFamily: 'monospace' }}>
            {evaluation.display}
          </Text>

          <Box
            mt="lg"
            pt="md"
            style={{ borderTop: '2px solid var(--mantine-color-blue-5)' }}
          >
            <Group justify="space-between" align="center">
              <Text fw={700} size="lg">Result:</Text>
              <Group>
                <Text fw={700} size="xl" style={{ fontFamily: 'monospace' }}>
                  {formatNumber(evaluation.result)}
                </Text>
                <CopyButton value={evaluation.result.toString()}>
                  {({ copied, copy }) => (
                    <Button
                      color={copied ? 'teal' : 'blue'}
                      onClick={copy}
                      size="xs"
                    >
                      {copied ? 'Copied' : 'Copy'}
                    </Button>
                  )}
                </CopyButton>
              </Group>
            </Group>
          </Box>
        </Box>
      )}
    </>
  );
};

export default Calculator;
